/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Canonical IANA timezone names for the event picker.
 *
 * `Intl.supportedValuesOf('timeZone')` is specified to return canonical zone
 * names, but older ICU builds still report the legacy Link names — this machine
 * offers `Asia/Calcutta` rather than `Asia/Kolkata`, and the engine does not
 * canonicalise on its own. Left alone, the same zone would be stored as a
 * different string depending on which machine the admin happened to use, and
 * `event.timezone` is a plain text column that would carry the difference
 * straight into the data.
 *
 * The rewrite is deliberately an explicit, readable table rather than a silent
 * transformation: every entry below is an IANA Link record, and anything not
 * listed passes through untouched.
 */

/** Legacy IANA Link names → the canonical zone they point at. */
const ALIASES = {
  'Africa/Asmera': 'Africa/Asmara',
  'Africa/Timbuktu': 'Africa/Bamako',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'Asia/Ashkhabad': 'Asia/Ashgabat',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Macao': 'Asia/Macau',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Australia/Currie': 'Australia/Hobart',
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Uzhgorod': 'Europe/Kyiv',
  'Europe/Zaporozhye': 'Europe/Kyiv',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

/** Used when the environment reports no zones at all. */
const FALLBACK_ZONES = [
  'UTC', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore',
  'Europe/London', 'America/New_York',
];

/** Returns the canonical name for one zone, or the input if it is unknown. */
export function canonicalTimezone(zone) {
  return ALIASES[zone] || zone;
}

/**
 * Canonicalises, de-duplicates and sorts the zones an environment reports, so
 * the picker offers the same names and the same order everywhere.
 */
export function canonicalTimezoneList(zones) {
  const source = (zones && zones.length) ? zones : FALLBACK_ZONES;
  return Array.from(new Set(source.map(canonicalTimezone))).sort();
}

/** The zones this browser knows about, canonicalised. */
export function supportedTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return canonicalTimezoneList(Intl.supportedValuesOf('timeZone'));
    }
  } catch {
    /* fall through to the fallback list */
  }
  return canonicalTimezoneList([]);
}

/**
 * The UTC offset of `zone` right now, as "GMT+5:30".
 *
 * Read from Intl rather than a table so it follows daylight saving on its own —
 * a fixed table would show London an hour out for half the year. Returns '' when
 * the engine cannot format the zone, and the caller simply shows the name alone.
 */
export function timezoneOffset(zone, now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, timeZoneName: 'shortOffset',
    }).formatToParts(now);
    const found = parts.find((p) => p.type === 'timeZoneName');
    return found ? found.value : '';
  } catch {
    return '';
  }
}

/**
 * The browser's own zone, canonicalised, or '' when it cannot be read.
 *
 * Offered as a shortcut rather than selected automatically: an event's timezone
 * is the event's, not the organiser's, and someone in Bangalore may well be
 * creating an event in Mysuru — or in another country.
 */
export function detectedTimezone() {
  try {
    return canonicalTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  } catch {
    return '';
  }
}

/**
 * Timezones as <optgroup>-shaped options: a "Detected" group first, then one
 * group per region.
 *
 * 418 zones in one flat list is the problem this solves. Two things make it
 * navigable: the region groups turn one long scroll into a short one, and the
 * offset in each label lets someone recognise the right zone without knowing
 * which city IANA named it after.
 *
 * The offset is placed AFTER the name, never before. A native select jumps to
 * the option whose label starts with what you type, so a leading "(GMT+5:30)"
 * would break typing "asia" to get there — the fastest route for anyone who
 * knows their zone, and the one worth protecting.
 */
export function groupedTimezoneOptions(zones = supportedTimezones(), detected = detectedTimezone()) {
  const label = (zone) => {
    const offset = timezoneOffset(zone);
    return offset ? `${zone} · ${offset}` : zone;
  };

  const groups = new Map();
  zones.forEach((zone) => {
    const region = zone.includes('/') ? zone.slice(0, zone.indexOf('/')) : 'Other';
    if (!groups.has(region)) groups.set(region, []);
    groups.get(region).push({ value: zone, label: label(zone) });
  });

  const out = [];
  if (detected && zones.includes(detected)) {
    out.push({ group: 'Detected', options: [{ value: detected, label: label(detected) }] });
  }
  Array.from(groups.keys()).sort().forEach((region) => {
    out.push({ group: region, options: groups.get(region) });
  });
  return out;
}
